/**
 * Settings of a general template: name, description, where new pages go,
 * title pattern, questions, and "use for daily notes" (ADR 0024).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  TEMPLATE_CONFIG_PROP,
  readTemplateConfig,
  type TemplateConfig,
  type TemplateFieldType,
} from '@nexus/core';
import type { PageRecord } from '@nexus/store';
import { setTemplateConfig } from '@nexus/workspace';
import { useStore, useStoreQuery } from '../db/context';
import { Dialog } from '../components/Dialog';
import { searchCatalog, useCatalog } from '../state/catalog';
import { useTemplateUi } from './state';
import { ScheduleFields } from './schedule';
import { PageIcon } from '../components/PageIcon';

const field = 'rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';

export function TemplateSettingsDialog() {
  const { t } = useTranslation();
  const id = useTemplateUi((s) => s.settingsFor);
  const set = useTemplateUi((s) => s.set);
  return (
    <Dialog
      open={!!id}
      onOpenChange={(o) => !o && set({ settingsFor: null })}
      title={t('templates.settingsTitle')}
    >
      {id && <Settings key={id} id={id} onDone={() => set({ settingsFor: null })} />}
    </Dialog>
  );
}

function Settings({ id, onDone }: { id: string; onDone: () => void }) {
  const store = useStore();
  const loaded = useStoreQuery(
    async (s) => ({
      page: await s.getPage(id),
      config: readTemplateConfig((await s.getProps(id))[TEMPLATE_CONFIG_PROP]),
    }),
    [id],
  );
  if (!loaded?.page) return null;
  return <Form page={loaded.page} initial={loaded.config} store={store} onDone={onDone} />;
}

function Form({
  page,
  initial,
  store,
  onDone,
}: {
  page: PageRecord;
  initial: TemplateConfig;
  store: ReturnType<typeof useStore>;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const byId = useCatalog((s) => s.byId);
  const [name, setName] = useState(page.title);
  const [config, setConfig] = useState<TemplateConfig>(initial);
  const [targetQuery, setTargetQuery] = useState('');
  const update = (patch: Partial<TemplateConfig>) => setConfig((c) => ({ ...c, ...patch }));
  const target = config.targetId ? byId.get(config.targetId) : null;
  const options = targetQuery.trim() ? searchCatalog(targetQuery, 8) : [];

  const save = async () => {
    const clean: TemplateConfig = {
      ...config,
      fields: config.fields
        .filter((f) => f.label.trim())
        .map((f) => ({ ...f, label: f.label.trim() })),
    };
    if (name.trim() !== page.title) await store.updatePage(page.id, { title: name.trim() });
    await setTemplateConfig(store, page.id, clean);
    onDone();
  };

  return (
    <form
      className="flex flex-col gap-4 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label className="flex flex-col gap-1">
        {t('templates.name')}
        <input required value={name} onChange={(e) => setName(e.target.value)} className={field} />
      </label>
      <label className="flex flex-col gap-1">
        {t('templates.description')}
        <input
          value={config.description}
          onChange={(e) => update({ description: e.target.value })}
          className={field}
        />
      </label>

      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1">{t('templates.target')}</legend>
        <p className="text-xs text-fg-muted">
          {target
            ? t('templates.targetIs', { title: target.title || t('page.untitled') })
            : t('templates.targetTop')}
          {target && (
            <button
              type="button"
              onClick={() => update({ targetId: null })}
              className="ml-2 underline hover:text-fg"
            >
              {t('templates.targetClear')}
            </button>
          )}
        </p>
        <input
          value={targetQuery}
          onChange={(e) => setTargetQuery(e.target.value)}
          placeholder={t('templates.targetSearch')}
          aria-label={t('templates.targetSearch')}
          className={field}
        />
        {options.length > 0 && (
          <ul className="rounded-lg border border-border">
            {options.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => {
                    update({ targetId: p.id });
                    setTargetQuery('');
                  }}
                  className="flex w-full gap-2 px-2 py-1 text-left hover:bg-bg-hover"
                >
                  <span>
                    <PageIcon icon={p.icon} fallback={'📄'} />
                  </span>
                  <span className="truncate">{p.title || t('page.untitled')}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      <label className="flex flex-col gap-1">
        {t('templates.titlePattern')}
        <input
          value={config.titlePattern}
          onChange={(e) => update({ titlePattern: e.target.value })}
          placeholder="Vergadering {{date}} – {{title}}"
          className={field}
        />
        <span className="text-xs text-fg-muted">{t('templates.titlePatternHelp')}</span>
      </label>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1">{t('templates.questions')}</legend>
        {config.fields.map((f, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={f.label}
              aria-label={t('templates.questionLabel')}
              placeholder={t('templates.questionLabel')}
              onChange={(e) =>
                update({
                  fields: config.fields.map((x, j) =>
                    j === i ? { ...x, label: e.target.value } : x,
                  ),
                })
              }
              className={`${field} min-w-0 flex-1`}
            />
            <select
              value={f.type}
              aria-label={t('templates.questionType')}
              onChange={(e) =>
                update({
                  fields: config.fields.map((x, j) =>
                    j === i ? { ...x, type: e.target.value as TemplateFieldType } : x,
                  ),
                })
              }
              className={field}
            >
              <option value="text">{t('templates.type.text')}</option>
              <option value="date">{t('templates.type.date')}</option>
              <option value="page">{t('templates.type.page')}</option>
            </select>
            <button
              type="button"
              aria-label={t('templates.questionRemove')}
              onClick={() => update({ fields: config.fields.filter((_, j) => j !== i) })}
              className="rounded-md px-2 text-fg-muted hover:bg-bg-hover hover:text-danger"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => update({ fields: [...config.fields, { label: '', type: 'text' }] })}
          className="flex items-center gap-1 self-start rounded-md px-2 py-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <Plus className="size-4" />
          {t('templates.questionAdd')}
        </button>
      </fieldset>

      <ScheduleFields value={config.schedule} onChange={(schedule) => update({ schedule })} />

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={config.daily}
          onChange={(e) => update({ daily: e.target.checked })}
        />
        {t('templates.daily')}
      </label>

      <details className="rounded-md bg-bg-subtle p-2 text-xs text-fg-muted">
        <summary className="cursor-pointer">{t('templates.variables')}</summary>
        <p className="mt-1 whitespace-pre-line">{t('templates.variablesHelp')}</p>
      </details>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="rounded-md px-3 py-1.5 hover:bg-bg-hover">
          {t('templates.cancel')}
        </button>
        <button
          type="submit"
          className="rounded-full bg-accent px-3 py-1.5 font-medium text-accent-fg"
        >
          {t('templates.save')}
        </button>
      </div>
    </form>
  );
}
